import React, { useEffect } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import StripeElementField from './StripeElementField';
import {
  composeValidators,
  fullCardNumberStripeElement,
  fullCVCStripeElement,
  fullExpireDateStripeElement,
  required,
  validStripeElement,
} from '../../../utils/formFieldValidators';
import { TextField } from '../../01_atoms/FormElements';
import { PAYMENT_MODE } from '../../../utils/payments';
import fetch from '../../../utils/fetch';
import { BACKEND_URL, RG } from '../../../utils/constants';

import styles from './index.module.scss';

const debug = require('debug')('cw:CreditCardStripeForm');

const cancelPaymentOnDrupal = (orderId, paymentId) => {
  fetch(`${BACKEND_URL}/commerce/payment/void/${orderId}/${paymentId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Helper function to complete payment after the order has been created.
export async function completeCardPayment(
  stripe,
  elements,
  billingDetails,
  orderId,
  donationType,
  captchaToken,
) {
  try {
    // Create payment and receive client secret.
    const responseCreate = await fetch(`${BACKEND_URL}/commerce/payment/create/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        captchaToken,
        gateway: `decoupled_stripe_credit_card_${donationType === RG ? 'rg_' : ''}${PAYMENT_MODE}`,
        type: 'credit_card',
      },
    });

    const paymentId = responseCreate.body.payment_id[0].value;
    const clientSecret = responseCreate.body.remote_id[0].value;

    const cardElement = elements.getElement('cardNumber');

    // Let Stripe to finalize payment.
    const { error } =
      donationType === RG
        ? await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: billingDetails,
            },
          })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: billingDetails,
            },
          });

    if (error) {
      // Customise error messages by error codes.
      let additionalErrorMessages = {
        payment_intent_authentication_failure:
          'This payment needs to be approved on your registered device. Please check and try again.',
        authentication_required:
          'This payment needs to be approved on your registered device. Please check and try again.',
        insufficient_funds:
          'Your bank has indicated that they cannot complete this transaction. Please check your account or with your bank.',
        incorrect_number: 'Your card number is incorrect. Please check and try again.',
        incorrect_cvc: 'Your CVC number is incorrect. Please check and try again.',
      };

      // Custom error message for particular error code.
      // See all error codes on the https://stripe.com/docs/error-codes
      if (Object.keys(additionalErrorMessages).includes(error.code)) {
        debug('Stripe.js could not handle card.', error);

        cancelPaymentOnDrupal(orderId, paymentId);
        return {
          type: 'error',
          errorMessage: additionalErrorMessages[error.code],
        };
      }

      // Custom error message in case when card was declined and has declined code.
      // See all declined codes on the https://stripe.com/docs/declines/codes
      if (error.decline_code && Object.keys(additionalErrorMessages).includes(error.decline_code)) {
        debug('Stripe.js could not handle card.', error);
        cancelPaymentOnDrupal(orderId, paymentId);
        return {
          type: 'error',
          errorMessage: additionalErrorMessages[error.decline_code],
        };
      }

      // Sometimes payment confirmation is triggered twice and the second attempt
      // generates error "payment_intent_unexpected_state".
      // See https://www.pivotaltracker.com/story/show/179464448
      const intentName = donationType === RG ? 'setup_intent' : 'payment_intent';
      const alreadySuccess = error[intentName] && error[intentName].status === 'succeeded';
      if (!alreadySuccess) {
        debug('Stripe.js could not handle card.', error);
        cancelPaymentOnDrupal(orderId, paymentId);
        return {
          type: 'error',
          errorMessage:
            'There has been an issue with your payment. Please re-check your payment details or check with your bank.',
        };
      }
    }

    // Inform the backend about successful payment and finalize donation on Drupal.
    await fetch(`${BACKEND_URL}/commerce/payment/capture/${orderId}/${paymentId}`, {
      method: 'POST',
      // Requests with 409 status code won't be sent to Rollbar and the request
      // won't trigger errors.
      ignoreStatusCodes: [409],
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Redirect to Thank You page.
    return {
      type: 'success',
      orderId: orderId,
    };
  } catch (error) {
    debug('Could not process Stripe payment.', error);
    // Show message to the user.
    return {
      type: 'error',
      errorMessage: '',
    };
  }
}

const CreditCardStripeForm = ({ onCompleteCardPayment, orderId = null }) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!orderId) {
      return;
    }
    if (onCompleteCardPayment) {
      onCompleteCardPayment(stripe, elements, orderId);
    }
  }, [orderId, stripe]);

  if (!stripe) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles['credit-card-stripe-form']}>
      <StripeElementField
        elementType="cardNumber"
        label="Card number"
        placeholder="0000 0000 0000 0000"
        id="stripe-cc-number"
        name="payment_options.card_number_status"
        info="Accepted card types are Visa, Visa Debit, Maestro and Mastercard."
        validate={composeValidators(
          required('Please enter your card number in order to proceed'),
          validStripeElement,
          fullCardNumberStripeElement,
        )}
        isRequired
      />

      <TextField
        name="payment_options.name"
        label="Cardholder name"
        placeholder="Jane Bloggs"
        validate={required('Please enter cardholder name in order to proceed')}
        isRequired
        autoComplete="cc-name"
        className="stripe-placeholder"
      />

      <div className={styles['credit-card-stripe-form__inline']}>
        <StripeElementField
          elementType="cardExpiry"
          label="Expiry date"
          placeholder="MM/YY"
          id="stripe-cc-expiry"
          name="payment_options.card_expiry_status"
          validate={composeValidators(
            required('Please add expiration date of your card'),
            validStripeElement,
            fullExpireDateStripeElement,
          )}
          isRequired
        />
        <StripeElementField
          elementType="cardCvc"
          label="CVV"
          placeholder="000"
          id="stripe-cc-cvv"
          name="payment_options.card_cvv_status"
          tooltip="Usually the last 3 digits in the signature area on the back of the card."
          validate={composeValidators(
            required(
              'Please add CVC number. This is usually the last 3 digits on the back of your card.',
            ),
            validStripeElement,
            fullCVCStripeElement,
          )}
          isRequired
        />
      </div>
    </div>
  );
};

CreditCardStripeForm.propTypes = {
  orderId: PropTypes.number,
  onCompleteCardPayment: PropTypes.func.isRequired,
};

export default CreditCardStripeForm;
